import { useCallback, useMemo } from 'react';

import { computeSmswiserTemplateId } from '@/utils/smswiser-template-id';

import { NO_TAGS_VALUE } from '../../../shared';

interface UseTextsFilterParams {
  textsList: string[];
  search: string;
  tagFilter: string;
  tagsByTextId?: Record<string, string[]>;
  textToTemplateId?: Record<string, string>;
  selectedTexts: string[];
}

export function useTextsFilter(params: UseTextsFilterParams) {
  const { textsList, search, tagFilter, tagsByTextId, textToTemplateId, selectedTexts } = params;

  const availableTextTags = useMemo(() => {
    if (!tagsByTextId) return [];
    const set = new Set<string>();
    for (const tags of Object.values(tagsByTextId)) {
      for (const t of tags ?? []) if (t && String(t).trim()) set.add(String(t).trim());
    }
    return [...set].sort();
  }, [tagsByTextId]);

  const getTagsForText = useCallback(
    (text: string): string[] | undefined => {
      if (!tagsByTextId) return undefined;
      const templateId = textToTemplateId?.[text] ?? computeSmswiserTemplateId(text);
      return tagsByTextId[templateId];
    },
    [tagsByTextId, textToTemplateId],
  );

  const filtered = useMemo(() => {
    let list = textsList.filter((t) => t.toLowerCase().includes(search.toLowerCase()));
    if (tagFilter && tagsByTextId) {
      if (tagFilter === NO_TAGS_VALUE) {
        list = list.filter((t) => {
          const tid = textToTemplateId?.[t] ?? computeSmswiserTemplateId(t);
          return !tagsByTextId[tid]?.length;
        });
      } else {
        const q = tagFilter.toLowerCase();
        list = list.filter((t) => {
          const tid = textToTemplateId?.[t] ?? computeSmswiserTemplateId(t);
          return tagsByTextId[tid]?.some((tag) => tag.toLowerCase() === q);
        });
      }
    }
    return [...list].sort((a, b) => {
      const aSel = selectedTexts.includes(a);
      const bSel = selectedTexts.includes(b);
      if (aSel === bSel) return 0;
      return aSel ? -1 : 1;
    });
  }, [textsList, search, tagFilter, tagsByTextId, textToTemplateId, selectedTexts]);

  return { availableTextTags, getTagsForText, filtered };
}
